"use client";

import React, { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function InfrastrutturaInvisibile() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: isVisualVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="infrastruttura-invisibile" className="dark bg-background text-foreground relative min-h-screen flex items-center py-32 overflow-hidden">
      {}
      <style>{`
        @keyframes scanline-vertical {
          0% { top: -5%; opacity: 0; }
          10% { opacity: 0.8; }
          90% { opacity: 0.8; }
          100% { top: 105%; opacity: 0; }
        }
        .animate-scanline {
          animation: scanline-vertical 8s linear infinite;
        }
      `}</style>

      {}
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]"></div>

      <div className="max-w-7xl mx-auto px-8 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col items-start transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Badge variant="default" className="rounded-none bg-primary text-primary-foreground font-mono tracking-widest text-xs py-2 px-4 mb-8 uppercase border border-primary/50 shadow-[0_0_15px_rgba(220,20,60,0.3)]">
              [CAPACITÀ OPERATIVA 2026: 3 PARTNER. SLOT RIMANENTI: 2]
            </Badge>

            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] text-foreground mb-8 tracking-tight">
              L'Infrastruttura Invisibile per Dominare l'Est.
            </h1>

            <p className="font-sans text-lg md:text-xl text-muted-foreground max-w-[500px] leading-relaxed">
              Ingegnerizziamo la penetrazione commerciale, blindiamo la logistica e ti sediamo ai tavoli dei grandi distributori in Russia, Kazakistan, Azerbaigian e CSI.
            </p>
          </div>

          {}
          <div ref={visualRef} className={`relative w-full aspect-square lg:aspect-auto lg:h-[700px] flex items-center justify-center transition-all duration-1000 delay-300 ease-out ${isVisualVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            {}
            <div className="relative w-full h-full border border-border/30 bg-card/20 backdrop-blur-sm overflow-hidden flex items-center justify-center p-8">
              
              {}
              <img src="https://vostoka.it/wp-content/uploads/2026/03/Vostoka-Logistica-e-Marketing-per-Russia-1-2.png" alt="Mappa astratta Eurasia con nodi operativi" className="w-full h-full object-contain opacity-80 filter drop-shadow-[0_0_20px_rgba(220,20,60,0.15)]" />

              {}
              <div className="absolute top-[30%] left-[40%] w-2 h-2 bg-primary rounded-full shadow-[0_0_10px_2px_rgba(220,20,60,0.8)] animate-pulse"></div>
              <div className="absolute top-[45%] left-[60%] w-3 h-3 bg-primary rounded-full shadow-[0_0_15px_3px_rgba(220,20,60,0.8)] animate-pulse" style={{
              animationDelay: '1s'
            }}></div>
              <div className="absolute top-[20%] left-[75%] w-1.5 h-1.5 bg-primary rounded-full shadow-[0_0_8px_1px_rgba(220,20,60,0.8)] animate-pulse" style={{
              animationDelay: '2s'
            }}></div>
              
              {}
              <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-40" preserveAspectRatio="none">
                <path d="M 40% 30% Q 50% 20% 60% 45%" fill="transparent" stroke="var(--primary)" strokeWidth="1" strokeDasharray="4 4" className="animate-pulse" />
                <path d="M 60% 45% Q 70% 30% 75% 20%" fill="transparent" stroke="var(--primary)" strokeWidth="1" strokeDasharray="4 4" className="animate-pulse" style={{
                animationDelay: '1.5s'
              }} />
              </svg>

              {}
              <div className="absolute left-0 right-0 h-[2px] bg-primary/80 shadow-[0_0_15px_3px_rgba(220,20,60,0.5)] animate-scanline z-20"></div>
              
              {}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50"></div>
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary/50"></div>
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary/50"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary/50"></div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}